import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Maybe } from 'true-myth';
import { z } from 'zod';
import type { StoreApi } from 'zustand';

import { ApplicationContext } from '~/shared/context/context';

import { useUserSettingsUiMutation } from '../../app/query-hooks/use-user-settings-ui';
import { useJobArchitectures } from '../api/use-job-architectures';
import type { UrlNavigationStore } from './url-navigation-store';
import { createUrlNavigationStore } from './url-navigation-store';

export const UrlNavigationContext = createContext<StoreApi<UrlNavigationStore> | null>(null);

const isEmptyOrUndefined = (id: string | undefined): boolean => {
    return is.undefined(id) || id === '';
};

export const useValidatedJobArchitectureId = (): string | undefined => {
    const { id } = useParams<{ id: string }>();

    const jobArchitectures = useJobArchitectures();

    if (isEmptyOrUndefined(id)) {
        return undefined;
    }

    const validationResult = z.uuid().safeParse(id);
    if (!validationResult.success) {
        return undefined;
    }

    if (jobArchitectures.isSuccess) {
        const architectureExists = jobArchitectures.data?.some((arch) => arch.id === id) ?? false;
        if (!architectureExists) {
            return undefined;
        }
    }

    return id;
};

export const updateSearchParams = (updater: (params: URLSearchParams) => void): void => {
    const newParams = new URLSearchParams(window.location.search);
    updater(newParams);
    const url = new URL(window.location.href);
    url.search = newParams.toString();
    window.history.replaceState({}, '', url.toString());
};

const useUrlNavigationStoreBuilder = (
    validJobArchitectureId: string | undefined,
): StoreApi<UrlNavigationStore> | undefined => {
    const navigate = useNavigate();
    const {
        applicationState: { uiSettings },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const {
        userSettingsUiMutation: { mutate },
    } = useUserSettingsUiMutation(validatingCobrainerApiFetcher);
    const storeRef = useRef<StoreApi<UrlNavigationStore> | undefined>(undefined);
    const previousIdRef = useRef<string | undefined>(undefined);

    if (is.nonEmptyString(validJobArchitectureId)) {
        const shouldCreateStore = is.undefined(storeRef.current);
        const hasIdChanged = previousIdRef.current !== validJobArchitectureId;

        if (shouldCreateStore || hasIdChanged) {
            storeRef.current = createUrlNavigationStore({
                activeJobArchitectureId: validJobArchitectureId,
                navigate,
                updateSearchParams,
            });
            previousIdRef.current = validJobArchitectureId;
        }
    }

    useEffect(() => {
        const store = storeRef.current;
        if (is.undefined(validJobArchitectureId) || is.undefined(store)) {
            return;
        }

        if (
            uiSettings.isJust &&
            !uiSettings.get('selectedJobArchitectureId').equals(Maybe.of(validJobArchitectureId))
        ) {
            mutate({ selectedJobArchitectureId: validJobArchitectureId });
        }

        const urlSearchParams = new URLSearchParams(window.location.search);
        store.getState().initializeSelectionFromUrl(urlSearchParams);
    }, [validJobArchitectureId, mutate, uiSettings]);

    return storeRef.current;
};

export const UrlNavigationContextProvider: FunctionComponent<{
    readonly children?: ReactNode;
    readonly redirectTo?: string;
}> = ({ children, redirectTo = '/job-architecture-v2' }) => {
    const validJobArchitectureId = useValidatedJobArchitectureId();

    const store = useUrlNavigationStoreBuilder(validJobArchitectureId);

    if (is.undefined(validJobArchitectureId)) {
        return <Navigate to={redirectTo} />;
    }

    if (is.undefined(store)) {
        return null;
    }

    return <UrlNavigationContext.Provider value={store}>{children}</UrlNavigationContext.Provider>;
};
